import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  MODAL_SURFACE_ALERT_DIALOG_PORTAL_HOST_NAME,
} from "@-/dev.improve.haniverse.components.ui.alert-dialog";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import {
  SettingsRow,
  SettingsRowGroup,
  SettingsRowSeparator,
} from "@-/dev.improve.haniverse.routes.nav.app.settings._components.settings-row";
import { useAboutSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.about.navigator";
import { useResetOnboarding } from "@-/dev.improve.haniverse.lib.slide.reset";
import { toast } from "@-/com.npmjs.sonner";
import { getAppVersionLabel } from "@-/dev.improve.haniverse.lib.app.version";
import { useLeaderResourceStatus } from "@-/dev.improve.haniverse.contexts.database.actor";
import type { ActorLeaderStatus } from "@-/dev.improve.haniverse.database.actor";
import { BUNDLE_BUILD_TIME, BUNDLE_GIT_COMMIT } from "@-/dev.improve.haniverse.lib.app.build-info";
import { useState } from "react";

/**
 * "Which tab holds the database?" — the first diagnostic question for any odd
 * multi-tab report on web (plan §8). Deliberately a static row, not a live
 * "reconnecting…" banner: a handover is a few hundred ms and should look like
 * ordinary loading, so a banner would only flicker.
 *
 * In development the value also carries this tab's proxied-query count — the
 * observability half of the per-query round-trip model (plan §7.1). It is read
 * at render time and is not reactive, so the row shows the count as of its last
 * render; that is enough to eyeball a storm.
 */
function getDatabaseLeaderLabel(leader: ActorLeaderStatus): string {
  const { status } = leader;
  let label: string;
  switch (status.state) {
    case "connected": {
      label = status.isLeader ? "Leader (this tab)" : "Follower";
      break;
    }
    case "electing": {
      label = "Electing…";
      break;
    }
    case "handover": {
      label = `Handover · ${status.queuedCount} queued`;
      break;
    }
    case "failed": {
      label = "Failed";
      break;
    }
  }
  if (typeof __DEV__ !== "undefined" && __DEV__) {
    return `${label} · ${leader.proxiedQueryCount.toLocaleString()} queries`;
  }
  return label;
}

function formatShortSha(sha: string): string {
  const trimmed = sha.trim();
  if (trimmed.length === 0) {
    return "";
  }
  // CI may inline the full 40-char SHA; local preval uses git rev-parse --short.
  return trimmed.length > 12 ? trimmed.slice(0, 7) : trimmed;
}

// When this JS bundle was built. `BUNDLE_BUILD_TIME` is a Unix timestamp (seconds) baked
// in at build time by babel-plugin-preval (see `lib/app/build-info`). `BUNDLE_GIT_COMMIT`
// is the matching short commit hash, shown as "(<sha>)" after the locale-formatted time.
// Falls back to "—" when the timestamp is `0` — under Jest the preval tag is shimmed to
// "" → `Number("")` is 0, and an unparseable value yields Invalid Date.
function getBuildLabel(): string {
  const shaSuffix = formatShortSha(BUNDLE_GIT_COMMIT);
  const shaLabel = shaSuffix.length > 0 ? ` (${shaSuffix})` : "";

  if (!BUNDLE_BUILD_TIME) {
    return shaSuffix.length > 0 ? `—${shaLabel}` : "—";
  }
  const builtAt = new Date(BUNDLE_BUILD_TIME * 1000);
  if (Number.isNaN(builtAt.getTime())) {
    return shaSuffix.length > 0 ? `—${shaLabel}` : "—";
  }
  const timeLabel = builtAt.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `${timeLabel}${shaLabel}`;
}

export function AboutSettingsSection({
  onAboutPress,
  showTitle = true,
}: { onAboutPress?: () => void; showTitle?: boolean } = {}) {
  const aboutPageNavigator = useAboutSettingsPageNavigator();
  const resetOnboardingMutation = useResetOnboarding();
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  // `null` on native and before the web connection opens — the row is web-only.
  const databaseLeader = useLeaderResourceStatus();

  const confirmResetOnboarding = () => {
    setIsResetConfirmOpen(false);
    resetOnboardingMutation.mutate(undefined, {
      onError: (error) => {
        showErrorToast("Could not reset onboarding", error);
      },
      onSuccess: () => {
        toast.success("Onboarding reset", {
          description: "Open the Learn tab to start the onboarding",
        });
      },
    });
  };

  return (
    <>
      <SettingsRowGroup title={showTitle ? "About" : undefined}>
        <SettingsRow
          accessibilityLabel="Open About settings"
          icon="MdInfoOutline"
          label="About"
          onPress={() => {
            if (onAboutPress) {
              onAboutPress();
            } else {
              aboutPageNavigator.push.mutate();
            }
          }}
          testID="settings-open-about"
          trailing={<Icon color="#9CA3AF" name="MdChevronRight" size={20} />}
          value={getAppVersionLabel()}
        />
        <SettingsRowSeparator />
        <SettingsRow
          disabled={resetOnboardingMutation.isPending}
          icon="MdRefresh"
          label="Reset onboarding"
          onPress={() => setIsResetConfirmOpen(true)}
          testID="settings-reset-onboarding"
        />
        <SettingsRowSeparator />
        <SettingsRow icon="MdSystemUpdate" label="Built" value={getBuildLabel()} />
        {databaseLeader === null ? null : (
          <>
            <SettingsRowSeparator />
            <SettingsRow
              icon="MdMemory"
              label="Database tab"
              testID="settings-database-leader"
              value={getDatabaseLeaderLabel(databaseLeader)}
            />
          </>
        )}
      </SettingsRowGroup>

      <AlertDialog onOpenChange={setIsResetConfirmOpen} open={isResetConfirmOpen}>
        {/*
          This section's only render site is the help/support sheet
          (`support-settings-options.tsx`), which is `accessibilityViewIsModal`. Portal the confirm
          dialog into that sheet's host rather than the app root, or it lands beside the modal and
          Android drops it from the accessibility tree.
        */}
        <AlertDialogContent portalHost={MODAL_SURFACE_ALERT_DIALOG_PORTAL_HOST_NAME}>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset onboarding?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll see the intro slides again from the beginning the next time you open the
              Learn tab. Your studied characters are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Text>Cancel</Text>
            </AlertDialogCancel>
            <AlertDialogAction
              onPress={confirmResetOnboarding}
              testID="settings-reset-onboarding-confirm"
            >
              <Text>Reset</Text>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
